import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Share as ShareIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import Barcode from 'react-barcode';
import { getSupabaseKeys } from '../login/api';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import LogoImg from '../../assets/logo.png';
import LogoCarteirinhaImg from '../../assets/logo_carteirinha.png';
import FundoCarteirinhaImg from '../../assets/fundo_carteirinha.png';

interface ClienteData {
  id: number;
  nome: string;
  cpf: string;
  email?: string;
  tipo_plano?: string;
}

// Utilitário para formatar CPF: 12345678900 → 123.456.789-00
const formatCPF = (cpf: string): string => {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

// Utilitário para formatar o label do plano
const formatPlanLabel = (plano?: string): string => {
  switch (plano) {
    case 'basico':   return 'Básico';
    case 'premium':  return 'Premium';
    case 'saude24h': return 'Saúde 24h';
    default:         return plano ?? '';
  }
};

// Utilitário para pegar valor do storage
const getStorageValue = async (key: string): Promise<string | null> => {
  if (Capacitor.isNativePlatform()) {
    const { value } = await Preferences.get({ key });
    return value;
  }
  return localStorage.getItem(key);
};

const Carteirinha: React.FC = () => {
  const [cliente, setCliente] = useState<ClienteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        setLoading(true);
        const userType = await getStorageValue('userType');
        const clienteId = await getStorageValue('clienteId');
        const dependenteId = await getStorageValue('dependenteId');

        if (!clienteId) {
          setError('Não foi possível identificar o paciente.');
          return;
        }

        const { url, serviceKey, serviceRoleKey } = getSupabaseKeys();
        const authKey = serviceRoleKey || serviceKey;

        // 1. Buscar dados do Cliente (Titular) - Necessário para CPF e Plano
        const resTitular = await fetch(
          `${url}/rest/v1/cliente?id=eq.${clienteId}&select=id,nome,cpf,email,tipo_plano`,
          {
            headers: {
              apikey: serviceKey,
              Authorization: `Bearer ${authKey}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!resTitular.ok) throw new Error(`Erro titular: ${resTitular.status}`);
        const dataTitular: ClienteData[] = await resTitular.json();

        if (dataTitular && dataTitular.length > 0) {
          const titular = dataTitular[0];

          // 2. Se for dependente, buscar o nome específico do dependente
          if (userType === 'dependente' && dependenteId) {
            const resDep = await fetch(
              `${url}/rest/v1/dependentes?id=eq.${dependenteId}&select=nome`,
              {
                headers: {
                  apikey: serviceKey,
                  Authorization: `Bearer ${authKey}`,
                  'Content-Type': 'application/json',
                },
              }
            );

            if (resDep.ok) {
              const dataDep = await resDep.json();
              if (dataDep && dataDep.length > 0) {
                // Sobrescreve apenas o nome para aparecer o do dependente no cartão
                setCliente({
                  ...titular,
                  nome: dataDep[0].nome
                });
              } else {
                setCliente(titular);
              }
            } else {
              setCliente(titular);
            }
          } else {
            // Se for o titular, usa os dados completos dele
            setCliente(titular);
          }
        } else {
          setError('Dados do paciente não encontrados.');
        }
      } catch (err: any) {
        console.error('[Carteirinha] Erro:', err);
        setError('Erro ao carregar carteirinha. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchDados();
  }, []);

  // Apenas os dígitos do CPF para o código de barras
  const cpfDigits = cliente?.cpf?.replace(/\D/g, '') ?? '';

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 4,
        pb: 6,
        minHeight: 'calc(100vh - 64px)',
      }}
    >
      {/* Header */}
      <Box sx={{ width: '100%', mb: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <CreditCardIcon sx={{ color: '#1976d2', fontSize: 28 }} />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#212121', letterSpacing: '-0.5px' }}
          >
            Minha Carteirinha
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Apresente este documento para identificação em atendimentos
        </Typography>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 8 }}>
          <CircularProgress size={48} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Carregando carteirinha...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Card */}
      {!loading && !error && cliente && (
        <Box sx={{ width: '100%' }}>
          {/* ─── CARTÃO ─── */}
          {/* ─── CARTÃO ─── */}
          <Box
            ref={cardRef}
            id="carteirinha-card"
            sx={{
              position: 'relative',
              width: '100%',
              borderRadius: '20px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 10px 30px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06)',
              overflow: 'hidden',
              p: 1.5,
              color: '#333',
              aspectRatio: '85.6 / 53.98',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {/* Fundo SVG */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                pointerEvents: 'none',
              }}
            >
              {/* <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
                <rect width="900" height="600" fill="#ffffff" />
                <path d="M0,450 Q150,600 400,530 T900,480 V600 H0 Z" fill="#cf111d" />
                <path d="M0,520 Q100,580 200,600 H0 Z" fill="#9e0b14" opacity="0.8" />
                <path d="M250,600 C400,520 600,430 900,500 V600 H250 Z" fill="#0d1e35" />
              </svg> */}
              <Box
                component="img"
                src={FundoCarteirinhaImg}
                alt="Fundo Carteirinha"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>

            {/* Topo do card: Chip + Logo Imagem */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                zIndex: 1,
              }}
            >
              {/* Lado Esquerdo: Chip + Texto (Agora escuros para fundo branco) */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {/* Logo Carteirinha - Topo Esquerdo */}
                <Box
                  component="img"
                  src={LogoCarteirinhaImg}
                  alt="Support Life Logo"
                  sx={{
                    height: { xs: '82px', sm: '120px' },
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />

                {/* Nome do paciente */}
                <Box sx={{ zIndex: 1, mt: 1 }}>
                  {/* <Typography
                    sx={{
                      fontSize: { xs: '0.55rem', sm: '0.65rem' },
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      mb: 0.3,
                      fontWeight: 600,
                    }}
                  >
                    Nome do Paciente
                  </Typography> */}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.2rem' },
                      letterSpacing: '0.03em',
                      color: '#1a1a1a',
                      // lineHeight: 1.2,
                    }}
                  >
                    {cliente.nome?.toUpperCase()}
                  </Typography>
                </Box>

                {/* Plano */}
                {cliente.tipo_plano && (
                  // <Box
                  //   sx={{
                  //     background: '#cf111d',
                  //     borderRadius: '20px',
                  //     px: 1.8,
                  //     py: 0.5,
                  //     mt: 1,
                  //   }}
                  // >
                  //   <Typography
                  //     sx={{
                  //       fontSize: { xs: '0.55rem', sm: '0.65rem' },
                  //       fontWeight: 800,
                  //       textTransform: 'uppercase',
                  //       letterSpacing: '0.12em',
                  //       color: '#FFFFFF',
                  //     }}
                  //   >
                  //     {cliente.tipo_plano}
                  //   </Typography>
                  // </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      color: '#1a1a1a',
                    }}
                  >
                    {formatPlanLabel(cliente.tipo_plano)}
                  </Typography>
                )}
              </Box>

              {/* Lado Direito: Info Block (Website, Pill, CPF) */}
              <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, mt: 1, zIndex: 1 }}>
                <Typography sx={{ color: '#cf111d', fontWeight: 800, fontSize: { xs: '.8rem', sm: '1.2rem' } }}>
                  www.supportlifemed.com.br
                </Typography>
                <Box sx={{ bgcolor: '#cf111d', px: 1.5, py: 0.2, borderRadius: '20px' }}>
                  <Typography sx={{ color: '#fff', fontSize: { xs: '0.8rem', sm: '1.2rem' }, fontWeight: 800, textTransform: 'uppercase' }}>
                    associado
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1.2rem' }, fontWeight: 800, color: '#333', fontFamily: '"Courier New", monospace' }}>
                  {formatCPF(cliente.cpf || '')}
                </Typography>
              </Box>
            </Box>


          </Box>

          {/* ─── VERSO / CÓDIGO DE BARRAS ─── */}
          <Box
            sx={{
              mt: 3,
              borderRadius: '20px',
              background: '#fff',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Código de Identificação
            </Typography>

            {cpfDigits && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  '& svg': { maxWidth: '100%', height: 'auto' },
                }}
              >
                <Barcode
                  value={cpfDigits}
                  format="CODE128"
                  width={1.8}
                  height={70}
                  fontSize={13}
                  margin={0}
                  background="#ffffff"
                  lineColor="#1a1a2e"
                  displayValue={false}
                />
              </Box>
            )}

            <Typography
              sx={{
                fontFamily: '"Courier New", monospace',
                fontWeight: 600,
                letterSpacing: '0.22em',
                fontSize: '0.9rem',
                color: '#1a1a2e',
              }}
            >
              {formatCPF(cliente.cpf || '')}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: 'center', mt: 0.5, lineHeight: 1.4 }}
            >
              Apresente este código para leitura automática nos terminais de atendimento
            </Typography>
          </Box>

          {/* ─── AÇÕES ─── */}
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Tooltip title="Compartilhar">
              <span>
                <IconButton
                  id="btn-compartilhar-carteirinha"
                  onClick={async () => {
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: 'Carteirinha Support Life',
                          text: `Carteirinha de ${cliente.nome} — CPF: ${formatCPF(cliente.cpf || '')}`,
                        });
                      } catch {
                        // cancelado pelo usuário
                      }
                    }
                  }}
                  disabled={!navigator.share}
                  sx={{
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.16)' },
                  }}
                >
                  <ShareIcon sx={{ color: '#1976d2' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default Carteirinha;
